import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import Button from "../common/Button";
import Input from "../common/Input";
import TextArea from "../Settings/TextArea";
import { ChecklistItem, ChecklistTemplate } from "../../db/schema";

interface CategorySection {
  id: string;
  name: string;
  items: ChecklistItem[];
  isCollapsed: boolean;
}

const ChecklistManager: React.FC = () => {
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<ChecklistTemplate | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] =
    useState<ChecklistTemplate | null>(null);

  // Form state for template
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [categories, setCategories] = useState<CategorySection[]>([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get("/api/agency/checklist-templates");
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to fetch checklist templates");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setSelectedTemplate(null);
    setTemplateName("");
    setTemplateDescription("");
    setCategories([]);
    setIsCreating(true);
    setIsEditing(false);
  };

  const handleEdit = (template: ChecklistTemplate) => {
    setSelectedTemplate(template);
    setTemplateName(template.name);
    setTemplateDescription(template.description || "");

    // Convert items to category-based structure
    const categoryMap = new Map<string, CategorySection>();

    template.items.forEach((item) => {
      const categoryName = item.category || "General";
      if (!categoryMap.has(categoryName)) {
        categoryMap.set(categoryName, {
          id: `cat_${Date.now()}_${categoryName}`,
          name: categoryName,
          items: [],
          isCollapsed: false,
        });
      }
      categoryMap.get(categoryName)!.items.push(item);
    });

    setCategories(Array.from(categoryMap.values()));
    setIsEditing(true);
    setIsCreating(false);
  };

  const handleSave = async () => {
    setShowPreview(false);
    if (!templateName.trim()) {
      toast.error("Template name is required");
      return;
    }

    if (categories.length === 0) {
      toast.error("At least one category with items is required");
      return;
    }

    // Check if all categories have at least one item
    const hasEmptyCategories = categories.some((cat) => cat.items.length === 0);
    if (hasEmptyCategories) {
      toast.error("All categories must have at least one item");
      return;
    }

    try {
      // Convert categories back to flat items array
      let order = 1;
      const items: ChecklistItem[] = [];

      categories.forEach((category) => {
        category.items.forEach((item) => {
          items.push({
            ...item,
            category: category.name,
            order: order++,
          });
        });
      });

      const templateData = {
        name: templateName,
        description: templateDescription,
        items,
        is_active: true,
      };

      let response;
      if (selectedTemplate) {
        response = await axios.patch(
          `/api/agency/checklist-templates/${selectedTemplate.id}`,
          templateData
        );
      } else {
        response = await axios.post(
          "/api/agency/checklist-templates",
          templateData
        );
      }

      toast.success(
        selectedTemplate
          ? "Template updated successfully"
          : "Template created successfully"
      );

      await fetchTemplates();
      setIsCreating(false);
      setIsEditing(false);
      setSelectedTemplate(response.data.template);
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to save template");
    }
  };

  const handleDelete = async (template: ChecklistTemplate) => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;

    try {
      await axios.delete(
        `/api/agency/checklist-templates/${templateToDelete.id}`
      );
      toast.success("Template deactivated successfully");
      await fetchTemplates();
      if (selectedTemplate?.id === templateToDelete.id) {
        setSelectedTemplate(null);
        setIsEditing(false);
        setIsCreating(false);
      }
    } catch (error) {
      console.error("Error deactivating template:", error);
      toast.error("Failed to deactivate template");
    } finally {
      setShowDeleteModal(false);
      setTemplateToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setTemplateToDelete(null);
  };

  const addNewCategory = () => {
    const newCategory: CategorySection = {
      id: `cat_${Date.now()}`,
      name: "",
      items: [],
      isCollapsed: false,
    };
    setCategories([...categories, newCategory]);
  };

  const updateCategory = (
    categoryId: string,
    updates: Partial<CategorySection>
  ) => {
    setCategories((cats) =>
      cats.map((cat) => (cat.id === categoryId ? { ...cat, ...updates } : cat))
    );
  };

  const deleteCategory = (categoryId: string) => {
    setCategories((cats) => cats.filter((cat) => cat.id !== categoryId));
  };

  const addNewItem = (categoryId: string) => {
    const newItem: ChecklistItem = {
      id: `item_${Date.now()}`,
      text: "",
      required: false,
      category: "", // Will be set when saving
      order: 0, // Will be set when saving
    };

    setCategories((cats) =>
      cats.map((cat) =>
        cat.id === categoryId ? { ...cat, items: [...cat.items, newItem] } : cat
      )
    );
  };

  const updateItem = (
    categoryId: string,
    itemIndex: number,
    updates: Partial<ChecklistItem>
  ) => {
    setCategories((cats) =>
      cats.map((cat) =>
        cat.id === categoryId
          ? {
              ...cat,
              items: cat.items.map((item, idx) =>
                idx === itemIndex ? { ...item, ...updates } : item
              ),
            }
          : cat
      )
    );
  };

  const deleteItem = (categoryId: string, itemIndex: number) => {
    setCategories((cats) =>
      cats.map((cat) =>
        cat.id === categoryId
          ? { ...cat, items: cat.items.filter((_, idx) => idx !== itemIndex) }
          : cat
      )
    );
  };

  const moveItem = (
    categoryId: string,
    itemIndex: number,
    direction: "up" | "down"
  ) => {
    const category = categories.find((cat) => cat.id === categoryId);
    if (!category) return;

    if (
      (direction === "up" && itemIndex === 0) ||
      (direction === "down" && itemIndex === category.items.length - 1)
    ) {
      return;
    }

    setCategories((cats) =>
      cats.map((cat) => {
        if (cat.id === categoryId) {
          const updatedItems = [...cat.items];
          const targetIndex =
            direction === "up" ? itemIndex - 1 : itemIndex + 1;
          [updatedItems[itemIndex], updatedItems[targetIndex]] = [
            updatedItems[targetIndex],
            updatedItems[itemIndex],
          ];
          return { ...cat, items: updatedItems };
        }
        return cat;
      })
    );
  };

  const moveCategory = (categoryIndex: number, direction: "up" | "down") => {
    if (
      (direction === "up" && categoryIndex === 0) ||
      (direction === "down" && categoryIndex === categories.length - 1)
    ) {
      return;
    }

    const updatedCategories = [...categories];
    const targetIndex =
      direction === "up" ? categoryIndex - 1 : categoryIndex + 1;
    [updatedCategories[categoryIndex], updatedCategories[targetIndex]] = [
      updatedCategories[targetIndex],
      updatedCategories[categoryIndex],
    ];
    setCategories(updatedCategories);
  };

  const createManchesterTemplate = () => {
    const manchesterCategories: CategorySection[] = [
      {
        id: "cat_prerequisites",
        name: "Start Here - Prerequisites",
        isCollapsed: false,
        items: [
          {
            id: "prereq_1",
            text: "Scene is secured",
            required: true,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
          {
            id: "prereq_2",
            text: "Medical aid rendered",
            required: true,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
          {
            id: "prereq_3",
            text: "Crime Scene Secured (interior, exterior, adjacent)",
            required: true,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
          {
            id: "prereq_4",
            text: "Legal authority confirmed (Consent / Search Warrant / Exigency)",
            required: true,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
        ],
      },
      {
        id: "cat_step1",
        name: "STEP 1: Identify & Document People",
        isCollapsed: false,
        items: [
          {
            id: "step1_1",
            text: "Names, address, phone, email",
            required: true,
            category: "",
            order: 0,
            description: "Victims, Suspects, Witnesses",
            additionalNotesEnabled: true,
          },
          {
            id: "step1_2",
            text: "Document statements for all",
            required: true,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
        ],
      },
      {
        id: "cat_step2",
        name: "STEP 2: Photograph the Scene",
        isCollapsed: false,
        items: [
          {
            id: "step2_1",
            text: "Scene (wide, medium, close)",
            required: true,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
          {
            id: "step2_2",
            text: "Injuries",
            required: false,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
          {
            id: "step2_3",
            text: "Evidence (with ID if needed)",
            required: true,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
        ],
      },
      {
        id: "cat_step3",
        name: "STEP 3: Handle Evidence",
        isCollapsed: false,
        items: [
          {
            id: "step3_1",
            text: "Label all evidence",
            required: true,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
          {
            id: "step3_2",
            text: "Tag for processing/seizure",
            required: true,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
          {
            id: "step3_3",
            text: "Secure appropriately",
            required: true,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
        ],
      },
      {
        id: "cat_step4",
        name: "STEP 4: Canvas & Document",
        isCollapsed: false,
        items: [
          {
            id: "step4_1",
            text: "Search: people, vehicles, surveillance (Fūsus, phone, etc.)",
            required: true,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
          {
            id: "step4_2",
            text: "Document: attempts, contacts, failures",
            required: true,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
        ],
      },
      {
        id: "cat_step5",
        name: "STEP 5: Paperwork & Permissions",
        isCollapsed: false,
        items: [
          {
            id: "step5_1",
            text: "Medical record consent",
            required: false,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
          {
            id: "step5_2",
            text: "Ask how injuries impact victim (for evidence of SBI)",
            required: false,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
          {
            id: "step5_3",
            text: "LAP/DV/case card given",
            required: false,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
          {
            id: "step5_4",
            text: "102 completed by backup officers (as applies)",
            required: false,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
        ],
      },
      {
        id: "cat_step6",
        name: "STEP 6: Share Intelligence",
        isCollapsed: false,
        items: [
          {
            id: "step6_1",
            text: "Issue BOLO (if needed)",
            required: false,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
          {
            id: "step6_2",
            text: "NCIC Entry (if needed)",
            required: false,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
          {
            id: "step6_3",
            text: "Dept Email (if needed)",
            required: false,
            category: "",
            order: 0,
            additionalNotesEnabled: true,
          },
        ],
      },
    ];

    setTemplateName("Investigation Checklist");
    setTemplateDescription("Standard investigation procedures checklist");
    setCategories(manchesterCategories);
    setIsCreating(true);
    setIsEditing(false);
    setSelectedTemplate(null);
  };

  if (loading) {
    return <div className="p-6">Loading checklists...</div>;
  }

  return (
    <div className="p-6 card border border-base-300 bg-base-100 mt-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-6 border-b border-base-300">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-2xl font-semibold text-base-content mb-2">
              Checklist Template Builder
            </h2>
            <p className="text-base-content/60">
              Create and manage investigation checklists for your officers.
            </p>
          </div>
        </div>

        {(isEditing || isCreating) && (
          <div className="flex gap-3">
            <Button
              onClick={() => setShowPreview(!showPreview)}
              variant="secondary"
              size="sm"
            >
              <EyeIcon className="w-4 h-4 mr-2" />
              {showPreview ? "Hide Preview" : "Preview"}
            </Button>
            <Button
              onClick={() => {
                setIsCreating(false);
                setIsEditing(false);
                setShowPreview(false); // Reset preview state when canceling
              }}
              variant="secondary"
              size="sm"
            >
              Cancel
            </Button>
            <Button onClick={handleSave} variant="primary" size="sm">
              Save Template
            </Button>
          </div>
        )}
      </div>

      <div className="flex gap-6">
        {/* Main Content Area */}
        <div
          className={`${
            showPreview ? "w-1/2" : "w-full"
          } transition-all duration-300`}
        >
          {!isEditing && !isCreating ? (
            /* Templates List View */
            <div className="bg-base-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-base-content">
                  Templates ({templates.length})
                </h3>
                <div className="flex gap-2">
                  <Button
                    onClick={createManchesterTemplate}
                    variant="secondary"
                    size="sm"
                  >
                    <DocumentDuplicateIcon className="w-4 h-4 mr-2" />
                    Use Sample Template
                  </Button>
                  <Button onClick={handleCreateNew} variant="primary" size="sm">
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Create New Template
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    className={`p-6 border rounded-lg cursor-pointer transition-all hover:shadow-md bg-base-100 ${
                      selectedTemplate?.id === template.id
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/40 shadow-md ring-1 ring-blue-200 dark:ring-blue-800"
                        : "border-base-300 hover:border-gray-300 dark:hover:border-base-content/30"
                    }`}
                    onClick={() => setSelectedTemplate(template)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-base-content mb-2">
                          {template.name}
                        </h4>
                        {template.description && (
                          <p className="text-sm text-gray-600 dark:text-base-content/70 mb-3 leading-relaxed">
                            {template.description}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-sm text-base-content/60">
                          <span className="flex items-center gap-1">
                            {template.items.length} items
                          </span>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              template.is_active
                                ? "bg-green-100 dark:bg-green-950/40 text-green-800 dark:text-green-400"
                                : "bg-gray-100 dark:bg-base-200 text-gray-600 dark:text-base-content/70"
                            }`}
                          >
                            {template.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(template);
                          }}
                          className="p-2 text-base-content/40 hover:text-blue-600 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/40 rounded-lg transition-colors"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(template);
                          }}
                          className="p-2 text-base-content/40 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-colors"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {templates.length === 0 && (
                  <div className="text-center text-base-content/60 py-12 bg-base-100 rounded-lg border-2 border-dashed border-gray-300 dark:border-base-300">
                    <DocumentDuplicateIcon className="w-12 h-12 mx-auto mb-4 text-base-content/40" />
                    <p className="text-lg mb-2">No templates created yet</p>
                    <p className="text-sm">
                      Get started by creating your first checklist template
                    </p>
                  </div>
                )}
              </div>

              {/* Selected Template Details */}
              {selectedTemplate && !isEditing && (
                <div className="mt-8 pt-6 border-t border-base-300">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-base-content">
                      {selectedTemplate.name}
                    </h3>
                    <Button
                      onClick={() => handleEdit(selectedTemplate)}
                      variant="primary"
                      size="sm"
                    >
                      <PencilIcon className="w-4 h-4 mr-2" />
                      Edit Template
                    </Button>
                  </div>

                  {selectedTemplate.description && (
                    <p className="text-gray-600 dark:text-base-content/70 mb-6">
                      {selectedTemplate.description}
                    </p>
                  )}

                  {/* Preview the checklist */}
                  <div className="bg-base-100 rounded-lg p-6 border border-base-300 shadow-sm">
                    <h4 className="font-medium mb-4 text-base-content">Preview</h4>
                    <div className="space-y-4">
                      {Object.entries(
                        selectedTemplate.items.reduce(
                          (
                            groups: { [key: string]: ChecklistItem[] },
                            item
                          ) => {
                            const category = item.category || "General";
                            if (!groups[category]) {
                              groups[category] = [];
                            }
                            groups[category].push(item);
                            return groups;
                          },
                          {}
                        )
                      ).map(([category, categoryItems]) => (
                        <div key={category} className="space-y-2">
                          <h5 className="font-semibold text-base-content text-sm uppercase tracking-wide flex items-center gap-2">
                            {category}
                          </h5>
                          <div className="ml-6 space-y-2">
                            {categoryItems.map((item) => (
                              <div
                                key={item.id}
                                className="flex items-start gap-2 text-sm"
                              >
                                <div className="w-4 h-4 border border-gray-300 dark:border-base-300 rounded mt-0.5"></div>
                                <div>
                                  <span
                                    className={`text-base-content ${
                                      item.required ? "font-medium" : ""
                                    }`}
                                  >
                                    {item.text}
                                    {item.required && (
                                      <span className="text-red-500 ml-1">
                                        *
                                      </span>
                                    )}
                                    {item.additionalNotesEnabled && (
                                      <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900/60 text-blue-800 dark:text-blue-300 px-2 py-0.5 rounded">
                                        Notes enabled
                                      </span>
                                    )}
                                  </span>
                                  {item.description && (
                                    <div className="text-xs text-base-content/60 mt-1">
                                      {item.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Editor View */
            <div className="bg-base-200 rounded-lg p-6 min-h-[400px]">
              {/* Template Settings */}
              <div className="mb-6 p-4 bg-base-100 border border-base-300 rounded-lg shadow-sm">
                <h3 className="font-semibold mb-4">Template Settings</h3>

                <div className="space-y-4">
                  <Input
                    title="Template Name"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="e.g., Investigation Checklist"
                    className="w-full !max-w-full"
                    variant="md"
                  />

                  <TextArea
                    label="Description"
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    placeholder="Describe when and how this checklist should be used"
                    rows={3}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Categories */}
              <div className="space-y-4">
                {categories.map((category, categoryIndex) => (
                  <div
                    key={category.id}
                    className="bg-base-100 border border-base-300 rounded-lg overflow-hidden shadow-sm"
                  >
                    {/* Category Header */}
                    <div className="bg-gradient-to-r from-base-200 to-base-300 p-4 border-b border-base-300">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => moveCategory(categoryIndex, "up")}
                              disabled={categoryIndex === 0}
                              className="p-0.5 text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 disabled:opacity-50"
                            >
                              <ChevronUpIcon className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() =>
                                moveCategory(categoryIndex, "down")
                              }
                              disabled={categoryIndex === categories.length - 1}
                              className="p-0.5 text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 disabled:opacity-50"
                            >
                              <ChevronDownIcon className="w-3 h-3" />
                            </button>
                          </div>

                          <span className="text-sm font-medium text-base-content/60 w-8">
                            #{categoryIndex + 1}
                          </span>

                          <Input
                            value={category.name}
                            onChange={(e) =>
                              updateCategory(category.id, {
                                name: e.target.value,
                              })
                            }
                            placeholder="Category name (e.g., STEP 1: Document People)"
                            className="flex-1 w-full !max-w-full"
                            variant="sm"
                          />

                          <button
                            onClick={() =>
                              updateCategory(category.id, {
                                isCollapsed: !category.isCollapsed,
                              })
                            }
                            className="p-2 hover:bg-base-300 rounded"
                          >
                            {category.isCollapsed ? (
                              <ChevronDownIcon className="w-4 h-4" />
                            ) : (
                              <ChevronUpIcon className="w-4 h-4" />
                            )}
                          </button>

                          <button
                            onClick={() => deleteCategory(category.id)}
                            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Category Items */}
                    {!category.isCollapsed && (
                      <div className="p-4 space-y-3 bg-base-200">
                        {category.items.map((item, itemIndex) => (
                          <div
                            key={item.id}
                            className="flex items-start gap-3 p-3 bg-base-100 border border-base-200 dark:border-base-300 rounded-lg hover:border-base-300 transition-colors shadow-sm"
                          >
                            <div className="flex flex-col gap-1 mt-1">
                              <button
                                onClick={() =>
                                  moveItem(category.id, itemIndex, "up")
                                }
                                disabled={itemIndex === 0}
                                className="p-0.5 text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 disabled:opacity-50"
                              >
                                <ChevronUpIcon className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() =>
                                  moveItem(category.id, itemIndex, "down")
                                }
                                disabled={
                                  itemIndex === category.items.length - 1
                                }
                                className="p-0.5 text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 disabled:opacity-50"
                              >
                                <ChevronDownIcon className="w-3 h-3" />
                              </button>
                            </div>

                            <span className="text-xs font-medium text-base-content/60 w-6 mt-2">
                              #{itemIndex + 1}
                            </span>

                            <div className="flex-1 space-y-2">
                              <Input
                                value={item.text}
                                onChange={(e) =>
                                  updateItem(category.id, itemIndex, {
                                    text: e.target.value,
                                  })
                                }
                                placeholder="Checklist item text"
                                className="w-full !max-w-full"
                                variant="sm"
                              />

                              <div className="space-y-2">
                                <div className="flex items-center gap-4">
                                  <label className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={item.required}
                                      onChange={(e) =>
                                        updateItem(category.id, itemIndex, {
                                          required: e.target.checked,
                                        })
                                      }
                                      className="w-4 h-4 text-blue-600 border-gray-300 dark:border-base-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-base-content/80">
                                      Required
                                    </span>
                                  </label>

                                  <label className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={item.additionalNotesEnabled || false}
                                      onChange={(e) =>
                                        updateItem(category.id, itemIndex, {
                                          additionalNotesEnabled: e.target.checked,
                                        })
                                      }
                                      className="w-4 h-4 text-blue-600 border-gray-300 dark:border-base-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-base-content/80">
                                      Enable Additional Notes
                                    </span>
                                  </label>
                                </div>

                                <Input
                                  value={item.description || ""}
                                  onChange={(e) =>
                                    updateItem(category.id, itemIndex, {
                                      description: e.target.value,
                                    })
                                  }
                                  placeholder="Description (optional)"
                                  variant="sm"
                                  className="text-xs max-w-none flex-1"
                                />
                              </div>
                            </div>

                            <button
                              onClick={() => deleteItem(category.id, itemIndex)}
                              className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}

                        <button
                          onClick={() => addNewItem(category.id)}
                          className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-base-300 rounded-lg text-gray-600 dark:text-base-content/70 hover:border-gray-400 dark:hover:border-base-content/30 hover:text-gray-700 dark:hover:text-base-content/80 hover:bg-base-200 transition-colors flex items-center justify-center gap-2"
                        >
                          <PlusIcon className="w-4 h-4" />
                          Add Item
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                <button
                  onClick={addNewCategory}
                  className="w-full py-4 border-2 border-dashed border-gray-300 dark:border-base-300 rounded-lg text-gray-600 dark:text-base-content/70 hover:border-gray-400 dark:hover:border-base-content/30 hover:text-gray-700 dark:hover:text-base-content/80 hover:bg-base-200 transition-colors flex items-center justify-center gap-2 bg-base-100"
                >
                  <PlusIcon className="w-5 h-5" />
                  Add Category
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Preview Panel */}
        {showPreview && (isEditing || isCreating) && (
          <div className="w-1/2 bg-base-200 rounded-lg overflow-hidden">
            <div className="sticky top-0 p-4 bg-blue-600 text-white">
              <h3 className="font-semibold flex items-center gap-2">
                <EyeIcon className="w-4 h-4" />
                Live Preview
              </h3>
              <p className="text-blue-200 text-sm mt-1">
                How officers will see this checklist
              </p>
            </div>

            <div className="p-4 h-full overflow-y-auto">
              <div className="bg-base-100 rounded-lg shadow-sm border border-base-300">
                {/* Preview Header */}
                <div className="bg-blue-900 text-white p-4 rounded-t-lg">
                  <h2 className="font-bold text-lg">
                    {templateName || "Untitled Template"}
                  </h2>
                  <p className="text-blue-200 text-sm mt-1">
                    {templateDescription}
                  </p>
                </div>

                {/* Preview Content */}
                <div className="p-4">
                  {categories.map((category) => (
                    <div key={category.id} className="mb-6">
                      <h3 className="font-semibold text-base-content mb-3 flex items-center gap-2">
                        {category.name || "Unnamed Category"}
                      </h3>
                      <div className="space-y-2">
                        {category.items.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-start gap-2 text-sm"
                          >
                            <div className="w-4 h-4 border border-gray-300 dark:border-base-300 rounded mt-0.5"></div>
                            <div>
                              <span className="text-base-content">
                                {item.text || "Empty item"}
                                {item.required && (
                                  <span className="text-red-500 ml-1">*</span>
                                )}
                                {item.additionalNotesEnabled && (
                                  <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900/60 text-blue-800 dark:text-blue-300 px-2 py-0.5 rounded">
                                    Notes enabled
                                  </span>
                                )}
                              </span>
                              {item.description && (
                                <div className="text-xs text-base-content/60 mt-1">
                                  {item.description}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {categories.length === 0 && (
                    <div className="text-center text-base-content/60 py-8">
                      <p>No categories added yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <dialog className={`modal ${showDeleteModal ? "modal-open" : ""}`}>
        <div className="modal-box max-w-md">
          <h3 className="font-bold text-lg">Delete Template</h3>
          <p className="py-4 text-gray-600 dark:text-base-content/70">
            Are you sure you want to delete the template "
            {templateToDelete?.name}"? It will no longer be available for new
            checklists, but existing checklists using this template will not be
            affected.
          </p>
          <div className="modal-action">
            <Button variant="outline" onClick={cancelDelete}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete Template
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={cancelDelete}>close</button>
        </form>
      </dialog>
    </div>
  );
};

export default ChecklistManager;
